import React from 'react'
import { styled } from '@mui/material'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useSortablePages } from './hooks/useSortablePages'
import { PageThumbnailItem, PageStatus } from './components/PageThumbnailItem'
import { VersionReportVersionPageFormModel } from '../../model/VersionReportVersionPageFormModel'

type PageThumbnailListProps = {
  pages: VersionReportVersionPageFormModel[]
  selectedPageNumber: number | null
  pageErrorMap: Map<number, boolean>
  pageUnconfiguredMap: Map<number, boolean>
  pageStatusMap: Map<number, PageStatus>
  onSelectPage: (pageNumber: number) => void
  onOpenConfigForPage: (pageNumber: number) => void
  onDeletePage: (pageNumber: number) => void
  onReorderPages: (reorderedPages: VersionReportVersionPageFormModel[]) => void
}

export const PageThumbnailList = ({
  pages,
  selectedPageNumber,
  pageErrorMap,
  pageUnconfiguredMap,
  pageStatusMap,
  onSelectPage,
  onOpenConfigForPage,
  onDeletePage,
  onReorderPages,
}: PageThumbnailListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const { handleDragStart, handleDragEnd, handleDragCancel } = useSortablePages(
    {
      items: pages,
      onReorder: onReorderPages,
      getId: page => page.pageNumber,
    }
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={pages.map(page => page.pageNumber)}
        strategy={verticalListSortingStrategy}
      >
        <ThumbnailContainer>
          {pages.map(page => (
            <PageThumbnailItem
              key={page.pageNumber}
              page={page}
              isSelected={page.pageNumber === selectedPageNumber}
              hasError={pageErrorMap.get(page.pageNumber) ?? false}
              isUnconfigured={pageUnconfiguredMap.get(page.pageNumber) ?? false}
              status={pageStatusMap.get(page.pageNumber) ?? 'unchanged'}
              onSelect={() => onSelectPage(page.pageNumber)}
              onOpenConfig={() => onOpenConfigForPage(page.pageNumber)}
              onDelete={() => onDeletePage(page.pageNumber)}
            />
          ))}
        </ThumbnailContainer>
      </SortableContext>
    </DndContext>
  )
}

const ThumbnailContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '8px',
})
